/****************
 * Live Prices hooks - consumes prices_update from WebSocket
 * Provides real-time mark prices for dashboard overlays
 ****************/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

export interface PriceData {
  /** Mark price. A number on the current feed; older payloads sent it as a
   *  string, which is why every reader parses rather than trusts it. */
  p: number | string;
  markPrice?: number | string;
  [key: string]: unknown;
}

export type LivePricesMap = Record<string, PriceData>;

/**
 * The mark price for a symbol, as a number, or 0 when there isn't one.
 *
 * Two call sites each wrote `parseFloat(priceData.p || priceData.markPrice ||
 * "0")`, which is three assumptions in one line: that `p` may be absent, that
 * `markPrice` is the fallback, and that either may arrive as a string. Only the
 * index signature made it compile, and it typed `markPrice` as `unknown`, so
 * both were errors the typecheck was not running to report.
 *
 * One definition instead, guarding the case the ternaries missed: a value that
 * parses to NaN reads as 0 rather than poisoning every arithmetic downstream of
 * it, which is how a PnL becomes "NaN" on screen.
 */
export function markPriceOf(price?: PriceData): number {
  if (!price) return 0;
  const raw = price.p ?? price.markPrice ?? 0;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : 0;
}

/****************
 * Hook to access all live prices from the WS cache
 ****************/

export function useLivePrices(): LivePricesMap {
  // REACTIVE subscription to the ['live-prices'] cache the WebSocket writes via
  // setQueryData. getQueryData alone is a one-time snapshot that never updates,
  // so consumers (live Open-Trade PnL) never re-rendered and stayed frozen at
  // $0. useQuery makes this component an observer, so every prices_update tick
  // re-renders it. 2026-07-05.
  const { data } = useQuery<LivePricesMap>({
    queryKey: ['live-prices'],
    queryFn: () => ({}),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return data ?? {};
}

/****************
 * Hook to access a single symbol's live price
 * Returns the price number or undefined if not available
 ****************/

export function useLivePrice(symbol: string): number | undefined {
  const prices = useLivePrices();
  const entry = prices[symbol];
  // undefined when there is no price for the symbol, as before -- callers use
  // `??` to fall back and a 0 would read as a real price of zero. Parsed rather
  // than returned raw, since the feed can send it as a string.
  return entry ? markPriceOf(entry) : undefined;
}

/****************
 * Hook that subscribes to live-prices query updates and triggers re-renders
 ****************/

export function useLivePricesSubscription() {
  const queryClient = useQueryClient();

  const subscribe = useCallback(() => {
    // Ensure the query exists in the cache (seed with empty object)
    if (!queryClient.getQueryData(['live-prices'])) {
      queryClient.setQueryData<LivePricesMap>(['live-prices'], {});
    }
  }, [queryClient]);

  useEffect(() => {
    subscribe();
  }, [subscribe]);

  // Return the current data - components using this will re-render
  // when useWebSocket sets new data via queryClient.setQueryData
  return queryClient.getQueryData<LivePricesMap>(['live-prices']) ?? {};
}
